import {
  AiSparkleIcon,
  ApiIcon,
  AssetIcon,
  BankIcon,
  BuildingIcon,
  CalculatorIcon,
  CapRollIcon,
  ChatIcon,
  DocumentIcon,
  DollarIcon,
  DrawCreditCardIcon,
  EditPenIcon,
  EntryIcon,
  FileUserIcon,
  FolderInIcon,
  HomeIcon,
  ListIcon,
  MenuIcon,
  NoteIcon,
  PrimaryIcon,
  RunIcon,
  SafetyIcon,
  TaskIcon,
  TreeDiagramIcon,
  UserEditIcon,
  UserIcon,
  WalletIcon,
} from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import { LabelVariant } from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS/LabelCDS.constants';

import { ActionStepType } from 'constants/aiChat';

export const ActionStepTypeIconMap = {
  [ActionStepType.DATA_READING]: DocumentIcon,
  [ActionStepType.EXECUTING_COMMAND]: RunIcon,
  [ActionStepType.MODIFYING_RECORD]: EditPenIcon,
  [ActionStepType.ANALYZING]: AssetIcon,
  [ActionStepType.GUIDELINE_LOOKUP]: NoteIcon,
  // TODO: replace with a dedicated brain/spark icon once design provides one
  [ActionStepType.MODEL_TURN]: ApiIcon,
  // TODO: replace with a dedicated specialist icon once design provides one
  [ActionStepType.SPECIALIST]: PrimaryIcon,
  [ActionStepType.SKILL_USE]: AiSparkleIcon,
  [ActionStepType.AGENT_DELEGATION]: TreeDiagramIcon,
};

// Fallback for steps that don't carry a kind (e.g. completed planner steps with
// only a description, like "Calculate ..."). Keeps the row visually consistent
// with typed steps.
export const DEFAULT_ACTION_STEP_ICON = AssetIcon;

// The chip carries only the verb; the step's title carries the subject
// (e.g. "borrower info"). A row reads visually as
// [icon] [READING] borrower info — verb on the chip, subject in the title.
export const ActionStepTypeLabelMap = {
  [ActionStepType.DATA_READING]: 'Reading',
  [ActionStepType.EXECUTING_COMMAND]: 'Running',
  [ActionStepType.MODIFYING_RECORD]: 'Updating',
  [ActionStepType.ANALYZING]: 'Analyzing',
  [ActionStepType.GUIDELINE_LOOKUP]: 'Reading',
  [ActionStepType.MODEL_TURN]: 'Thinking',
  [ActionStepType.SPECIALIST]: 'Specialist',
  [ActionStepType.SKILL_USE]: 'Skill',
  [ActionStepType.AGENT_DELEGATION]: 'Agent',
};

// DEV-2939: once a step reaches a terminal state, the present-continuous
// verb above ("Running", "Reading", ...) paired with its active color reads
// as "still in flight" right next to the green completion check. On a
// completed step we swap to the past-tense verb (and StatusLine forces the
// GRAY variant) so a finished row reads as done. Falls back to the
// present-tense label for any type without an explicit completed form.
export const ActionStepTypeCompletedLabelMap = {
  [ActionStepType.DATA_READING]: 'Read',
  [ActionStepType.EXECUTING_COMMAND]: 'Ran',
  [ActionStepType.MODIFYING_RECORD]: 'Updated',
  [ActionStepType.ANALYZING]: 'Analyzed',
  [ActionStepType.GUIDELINE_LOOKUP]: 'Read',
  [ActionStepType.MODEL_TURN]: 'Thought',
  [ActionStepType.SPECIALIST]: 'Specialist',
  [ActionStepType.SKILL_USE]: 'Skill',
  [ActionStepType.AGENT_DELEGATION]: 'Agent',
};

export const ActionStepTypeLabelVariantMap = {
  [ActionStepType.DATA_READING]: LabelVariant.GRAY,
  [ActionStepType.EXECUTING_COMMAND]: LabelVariant.PURPLE,
  [ActionStepType.MODIFYING_RECORD]: LabelVariant.GRAY,
  [ActionStepType.ANALYZING]: LabelVariant.GRAY,
  [ActionStepType.GUIDELINE_LOOKUP]: LabelVariant.GRAY,
  [ActionStepType.MODEL_TURN]: LabelVariant.BLUE,
  [ActionStepType.SPECIALIST]: LabelVariant.PURPLE,
  [ActionStepType.SKILL_USE]: LabelVariant.BLUE,
  [ActionStepType.AGENT_DELEGATION]: LabelVariant.PURPLE,
};

// LEN-36: how a write tool ran, badged next to the type chip so an operator can
// see at a glance whether a consequential tool (e.g. placing conditions)
// auto-applied with no confirmation or was approved by a human at the gate. The
// value comes from the persisted tool-call envelope (confirmationDisposition);
// read/framework calls carry none and render no badge.
const ToolConfirmationDisposition = {
  AUTO: 'auto',
  CONFIRMED: 'confirmed',
};

export const ToolConfirmationDispositionBadgeMap = {
  [ToolConfirmationDisposition.AUTO]: { title: 'Auto-applied', variant: LabelVariant.ORANGE },
  [ToolConfirmationDisposition.CONFIRMED]: { title: 'Confirmed', variant: LabelVariant.GREEN },
};

export const ActionStepToolIconMap = {
  get_borrowers: UserIcon,
  get_borrower: UserIcon,
  get_contacts: ChatIcon,
  get_loan_roles: UserEditIcon,
  get_loan_application: FileUserIcon,
  get_properties: HomeIcon,
  get_real_estate_owned: BuildingIcon,
  get_assets: WalletIcon,
  get_liabilities: DrawCreditCardIcon,
  get_incomes: DollarIcon,
  get_expenses: CalculatorIcon,
  get_declarations: ListIcon,
  get_conditions: TaskIcon,
  get_loan_tasks: TaskIcon,
  get_documents: FolderInIcon,
  get_closing_info: SafetyIcon,
  get_funding_info: BankIcon,
  get_secondary_financing: CapRollIcon,
  query_guidelines: NoteIcon,
  create_execution_plan: TreeDiagramIcon,
  update_execution_plan: TreeDiagramIcon,
  get_navigation_catalog: MenuIcon,
  open_loan_section: EntryIcon,
};
